// The hot and cold water risers of a part set into a room's wall (`wallRisers`:
// a shower mixer): two upright pipes in the wall, from the part up through the
// room's top, ending a set height over its highest point, their tops the part's
// `hot` and `cold` ports. Derived from the parts and the rooms every read, never
// stored: a part moved, a room's height or ceiling changed, the risers follow; a
// part out of every room's wall has none. Pure and DOM-free (test/risers.test.mjs;
// docs/claude/products.md, *The hand shower with mixer*).

import type {
  ProductInstance,
  Vec2,
} from '../shared/types';
import { roomCrown } from './ceiling';
import { instanceToWorld } from './geometry';
import {
  partSpan,
  roomBase,
} from './heights';
import { roomFaceAt } from './polyRoom';
import { inWallFace } from './wallCuts';

/** Which water a riser carries: also the id of the part's port at its top. */
export type RiserWater = 'cold' | 'hot';

/** The colour each riser is drawn in, every view alike. */
export const RISER_COLORS: Record<RiserWater, string> = { hot: '#d62828', cold: '#1f6fd6' };

/** One riser: its part, the room whose wall it is in, its water, its middle in
 *  plan (world), its radius, and its bottom and top above the ground (mm). */
export interface Riser {
  src: string;
  room: string;
  water: RiserWater;
  at: Vec2;
  r: number;
  z0: number;
  z1: number;
}

/** The room's highest top above the ground: its outside top at its highest point
 *  (a shaped ceiling's crown, the roof's thickness in), lifted by its own Height
 *  from ground. */
export function roomHighestTop(room: ProductInstance): number {
  return roomBase(room) + roomCrown(room);
}

/** A part's risers, when it is set into a room's wall (either face): none
 *  otherwise. `instances` holds the rooms. */
export function risersOf(instances: readonly ProductInstance[], p: ProductInstance): Riser[] {
  const spec = p.wallRisers;
  if (!spec || !(spec.diameter > 0)) return [];
  const face = inWallFace(p);
  if (!face) return [];
  const room = instances.find((r) => r.autoBenches && roomFaceAt(r, face.at, face.into));
  if (!room) return [];
  const y = p.depth / 2 - (p.inWall ?? 0) - spec.depth;
  const z0 = partSpan(p)[0] + spec.z, z1 = roomHighestTop(room) + spec.above;
  if (!(z1 > z0)) return [];
  return (['hot', 'cold'] as const).map((water) => {
    const side = water === 'hot' ? 1 : -1;
    return { src: p.id, room: room.id, water, at: instanceToWorld(p, { x: spec.x + (side * spec.spacing) / 2, y }), r: spec.diameter / 2, z0, z1 };
  });
}

/** Every riser of the parts set into the rooms' walls. */
export function partRisers(instances: readonly ProductInstance[]): Riser[] {
  return instances.flatMap((p) => (p.wallRisers ? risersOf(instances, p) : []));
}

/** The height above the ground of a part's port at a riser's top (`hot` /
 *  `cold`), when the part has that riser; null otherwise. */
export function riserPortHeight(instances: readonly ProductInstance[], p: ProductInstance, port: string): number | null {
  if (!p.wallRisers || (port !== 'hot' && port !== 'cold')) return null;
  return risersOf(instances, p).find((r) => r.water === port)?.z1 ?? null;
}

/** A riser's outline in plan (world), `n` points round it. */
export function riserRing(r: Pick<Riser, 'at' | 'r'>, n = 20): Vec2[] {
  return Array.from({ length: n }, (_, i) => ({ x: r.at.x + r.r * Math.cos((2 * Math.PI * i) / n), y: r.at.y + r.r * Math.sin((2 * Math.PI * i) / n) }));
}
